import React, { useEffect, useMemo, useState, useRef } from "react";
import { Camera, ArrowLeft, CalendarDays, MapPin, Clock, Check } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

export default function ActivityRegister() {
  const { t, i18n } = useTranslation();
  const { activityId } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [activity, setActivity] = useState(null);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const [sending, setSending] = useState(false);

  const [form, setForm] = useState({
    nom: "",
    prenom: "",
    email: "",
    telephone: "",
    date_naissance: "",
    ville_naissance: "",
    extra_question_answer: "",
  });

  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");

  const [consent, setConsent] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // =====================================================
  // GET ACTIVITY
  // =====================================================

  useEffect(() => {
    async function loadActivity() {
      try {
        setLoadingActivity(true);

        const res = await fetch(
          `/api/activity?action=getById&id=${encodeURIComponent(activityId)}`
        );

        const data = await res.json();

        if (!res.ok || !data.success) {
          setMessage(
            data?.message ||
              t("activityRegister.errors.activityNotFound")
          );
          setMessageType("error");
          return;
        }

        setActivity(data.activity);
        setRegistrationOpen(Boolean(data.registrationOpen));
      } catch (error) {
        console.error("GET ACTIVITY ERROR:", error);
        setMessage(t("activityRegister.errors.network"));
        setMessageType("error");
      } finally {
        setLoadingActivity(false);
      }
    }

    if (activityId) {
      loadActivity();
    }
  }, [activityId, t]);

  // =====================================================
  // CLEAN PHOTO PREVIEW
  // =====================================================

  useEffect(() => {
    return () => {
      if (photoPreview) {
        URL.revokeObjectURL(photoPreview);
      }
    };
  }, [photoPreview]);

  // =====================================================
  // FORM CHANGE
  // =====================================================

  function handleChange(e) {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  // =====================================================
  // PHOTO
  // =====================================================

  function handlePhotoChange(e) {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setMessage(t("activityRegister.errors.invalidPhoto"));
      setMessageType("error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage(t("activityRegister.errors.photoTooLarge"));
      setMessageType("error");
      return;
    }

    if (photoPreview) {
      URL.revokeObjectURL(photoPreview);
    }

    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
    setMessage("");
    setMessageType("");
  }

  // =====================================================
  // DYNAMIC QUESTION
  // =====================================================

  const currentLanguage = useMemo(() => {
    const lang = String(i18n.language || "fr").toLowerCase();

    if (lang.startsWith("ar")) return "ar";
    if (lang.startsWith("en")) return "en";

    return "fr";
  }, [i18n.language]);

  const dynamicQuestion = useMemo(() => {
    if (!activity) return "";

    const values = {
      fr: activity.extra_question_fr,
      en: activity.extra_question_en,
      ar: activity.extra_question_ar,
    };

    // Prefer current language, then fallback to another supplied translation.
    return (
      values[currentLanguage] ||
      values.fr ||
      values.en ||
      values.ar ||
      ""
    ).trim();
  }, [activity, currentLanguage]);

  const questionDirection =
    currentLanguage === "ar" ? "rtl" : "ltr";

  const hasDynamicQuestion = Boolean(dynamicQuestion);

  const yesLabel =
    currentLanguage === "ar"
      ? "نعم"
      : currentLanguage === "en"
        ? "Yes"
        : "Oui";

  const noLabel =
    currentLanguage === "ar"
      ? "لا"
      : currentLanguage === "en"
        ? "No"
        : "Non";

  // =====================================================
  // SUBMIT
  // =====================================================

  async function handleSubmit(e) {
    e.preventDefault();

    setMessage("");
    setMessageType("");

    if (!registrationOpen) {
      setMessage(t("activityRegister.errors.registrationClosed"));
      setMessageType("error");
      return;
    }

    if (hasDynamicQuestion && !form.extra_question_answer) {
      setMessage(
        currentLanguage === "ar"
          ? "يرجى الإجابة عن السؤال الإضافي."
          : currentLanguage === "en"
            ? "Please answer the additional question."
            : "Merci de répondre à la question supplémentaire."
      );
      setMessageType("error");
      return;
    }

    if (!photo) {
      setMessage(t("activityRegister.errors.photoRequired"));
      setMessageType("error");
      return;
    }

    if (!consent) {
      setMessage(t("activityRegister.errors.consentRequired"));
      setMessageType("error");
      return;
    }

    setSending(true);

    try {
      // ===================================================
      // UPLOAD PHOTO
      // ===================================================

      let photo_url = "";

      if (photo) {
        const reader = new FileReader();

        const base64 = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(photo);
        });

        const uploadRes = await fetch(
          "/api/activity?action=uploadPhoto",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              file: base64,
              fileName: photo.name,
              contentType: photo.type,
            }),
          }
        );

        const uploadData = await uploadRes.json();

        if (!uploadRes.ok || !uploadData.success) {
          setMessage(
            uploadData?.message ||
              t("activityRegister.errors.uploadFailed")
          );
          setMessageType("error");
          return;
        }

        photo_url = uploadData.photo_url;
      }

      // ===================================================
      // REGISTER
      // ===================================================

      const res = await fetch(
        "/api/activity?action=register",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            activity_id: activityId,
            nom: form.nom,
            prenom: form.prenom,
            email: form.email,
            telephone: form.telephone,
            date_naissance: form.date_naissance,
            ville_naissance: form.ville_naissance,
            photo_url,
            consent: true,

            // Dynamic question answer.
            // Backend will store it once the corresponding field is added.
            extra_question_answer:
              hasDynamicQuestion
                ? form.extra_question_answer
                : null,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok || !data.success) {
        if (data.code === "registration_closed") {
          setMessage(
            t("activityRegister.errors.registrationClosed")
          );
        } else if (data.code === "already_registered") {
          setMessage(
            t("activityRegister.errors.alreadyRegistered")
          );
        } else if (data.code === "activity_not_found") {
          setMessage(
            t("activityRegister.errors.activityNotFound")
          );
        } else if (data.code === "required") {
          setMessage(t("activityRegister.errors.required"));
        } else {
          setMessage(
            data?.message ||
              t("activityRegister.errors.server")
          );
        }

        setMessageType("error");
        return;
      }

      setMessage(t("activityRegister.success"));
      setMessageType("success");

      setForm({
        nom: "",
        prenom: "",
        email: "",
        telephone: "",
        date_naissance: "",
        ville_naissance: "",
        extra_question_answer: "",
      });

      setPhoto(null);
      setPhotoPreview("");
      setConsent(false);
    } catch (error) {
      console.error(
        "ACTIVITY REGISTRATION ERROR:",
        error
      );

      setMessage(
        t("activityRegister.errors.network")
      );
      setMessageType("error");
    } finally {
      setSending(false);
    }
  }

  // =====================================================
  // LOADING
  // =====================================================

  if (loadingActivity) {
    return (
      <div
        className="
          min-h-screen
          flex
          items-center
          justify-center
          bg-gradient-to-br
          from-slate-950
          via-slate-900
          to-indigo-950
        "
      >
        <p className="text-white text-sm">
          {t("activityRegister.loading")}
        </p>
      </div>
    );
  }

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div className="ar-root">
      <style>{`
        .ar-root {
          --ink: #111827;
          --ink-soft: #1e293b;
          --paper: #f8fafc;
          --paper-line: #dbe2ea;
          --text: #172033;
          --text-soft: #64748b;
          --accent: #6366f1;
          --accent-deep: #4f46e5;
          --danger: #dc2626;

          min-height: 100vh;
          padding: 48px 20px 80px;
          color: var(--text);

          background:
            radial-gradient(
              circle at 12% 8%,
              rgba(99, 102, 241, 0.16),
              transparent 32%
            ),
            radial-gradient(
              circle at 88% 92%,
              rgba(124, 58, 237, 0.12),
              transparent 34%
            ),
            #eef2ff;

          font-family:
            Inter,
            ui-sans-serif,
            system-ui,
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            sans-serif;

          display: flex;
          justify-content: center;
        }

        .ar-shell {
          width: 100%;
          max-width: 640px;
        }

        .ar-back {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: none;
          border: none;
          color: #64748b;
          font-size: 14px;
          cursor: pointer;
          padding: 0;
          margin-bottom: 22px;
          transition: color 0.15s ease;
        }

        .ar-back:hover {
          color: #111827;
        }

        /* ---------- Ticket hero ---------- */

        .ar-ticket {
          position: relative;
          background:
            radial-gradient(
              circle at 18% 20%,
              rgba(255, 255, 255, 0.06),
              transparent 45%
            ),
            var(--ink);

          border-radius: 20px 20px 0 0;
          color: #f8fafc;

          display: grid;
          grid-template-columns: 1fr 168px;

          overflow: hidden;
          animation: ar-rise 0.5s ease both;
        }

        @keyframes ar-rise {
          from {
            opacity: 0;
            transform: translateY(14px);
          }

          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .ar-ticket::before {
          content: "";
          position: absolute;
          inset: 0;

          background-image:
            radial-gradient(
              rgba(255, 255, 255, 0.55) 1px,
              transparent 1.4px
            );

          background-size: 26px 26px;
          opacity: 0.15;
          pointer-events: none;
        }

        .ar-ticket-main {
          position: relative;
          padding: 34px 28px 30px 30px;
          z-index: 1;
        }

        .ar-ref {
          font-size: 12px;
          letter-spacing: 0.02em;
          color: #a5b4fc;
          margin-bottom: 14px;
        }

        .ar-moon {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background: var(--accent);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #ffffff;
          margin-bottom: 16px;
          box-shadow: 0 8px 25px rgba(99, 102, 241, 0.35);
        }

        .ar-title {
          font-family:
            ui-serif,
            Georgia,
            Cambria,
            "Times New Roman",
            serif;

          font-weight: 600;
          font-size: 34px;
          line-height: 1.08;
          margin: 0 0 4px;
          letter-spacing: -0.02em;
        }

        .ar-subtitle {
          font-size: 15px;
          color: #c7d2fe;
          margin: 0 0 16px;
        }

        .ar-desc {
          font-size: 14px;
          line-height: 1.6;
          color: #dbe4ff;
          max-width: 46ch;
          margin: 0;
        }

        .ar-stub {
          position: relative;
          z-index: 1;
          padding: 30px 22px;
          display: flex;
          flex-direction: column;
          gap: 16px;
          justify-content: center;
        }

        .ar-stub::before {
          content: "";
          position: absolute;
          left: 0;
          top: 14px;
          bottom: 14px;
          border-left: 1.5px dashed rgba(255, 255, 255, 0.25);
        }

        .ar-stub-item {
          display: flex;
          align-items: flex-start;
          gap: 9px;
          font-size: 12.5px;
          line-height: 1.4;
          color: #e2e8f0;
        }

        .ar-stub-item svg {
          flex-shrink: 0;
          margin-top: 1px;
          color: #a5b4fc;
        }

        .ar-hole {
          position: absolute;
          left: -1px;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #eef2ff;
        }

        .ar-hole.top {
          top: -8px;
        }

        .ar-hole.bottom {
          bottom: -8px;
        }

        /* ---------- Closed notice ---------- */

        .ar-closed {
          background: var(--paper);
          border-radius: 0 0 20px 20px;
          padding: 26px 30px;
          border-top: 1.5px dashed var(--paper-line);
        }

        .ar-closed-title {
          font-weight: 600;
          color: var(--danger);
          margin: 0 0 4px;
        }

        .ar-closed-body {
          font-size: 14px;
          color: var(--text-soft);
          margin: 0;
        }

        /* ---------- Form card ---------- */

        .ar-form {
          background: var(--paper);
          border-radius: 0 0 20px 20px;
          padding: 30px 30px 34px;
          border-top: 1.5px dashed var(--paper-line);
        }

        .ar-form-head {
          margin-bottom: 22px;
        }

        .ar-form-head h2 {
          font-family:
            ui-serif,
            Georgia,
            Cambria,
            "Times New Roman",
            serif;

          font-weight: 500;
          font-size: 21px;
          margin: 0 0 4px;
          color: var(--text);
        }

        .ar-form-head p {
          font-size: 13.5px;
          color: var(--text-soft);
          margin: 0;
        }

        .ar-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 20px;
        }

        @media (max-width: 520px) {
          .ar-row {
            grid-template-columns: 1fr;
            gap: 18px;
          }
        }

        .ar-field {
          display: flex;
          flex-direction: column;
        }

        .ar-field label {
          font-size: 12.5px;
          color: var(--text-soft);
          margin-bottom: 6px;
        }

        .ar-field input {
          font-family: inherit;
          font-size: 15px;
          color: var(--text);

          background: transparent;
          border: none;
          border-bottom: 1.5px solid var(--paper-line);

          padding: 6px 2px 9px;
          outline: none;

          transition: border-color 0.15s ease;
        }

        .ar-field input:focus {
          border-color: var(--accent);
        }

        .ar-field input::placeholder {
          color: #94a3b8;
        }

        /* ---------- Dynamic question ---------- */

        .ar-question-block {
          margin: 4px 0 24px;
          padding: 18px 0 2px;
          border-top: 1px solid rgba(219, 226, 234, 0.75);
        }

        .ar-question-label {
          display: block;
          font-size: 14px;
          line-height: 1.6;
          font-weight: 600;
          color: var(--text);
          margin-bottom: 14px;
        }

        .ar-question-options {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        .ar-question-option {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          cursor: pointer;
          padding: 10px 16px;
          border: 1px solid var(--paper-line);
          border-radius: 999px;
          background: #ffffff;
          color: #334155;
          font-size: 13.5px;
          transition:
            border-color 0.15s ease,
            background 0.15s ease,
            transform 0.1s ease;
        }

        .ar-question-option:hover {
          border-color: rgba(99, 102, 241, 0.45);
          background: #eef2ff;
        }

        .ar-question-option:active {
          transform: scale(0.98);
        }

        .ar-question-option input {
          width: 15px;
          height: 15px;
          margin: 0;
          accent-color: var(--accent);
        }

        .ar-question-option.selected {
          border-color: rgba(99, 102, 241, 0.55);
          background: #eef2ff;
          color: #3730a3;
        }

        /* ---------- Photo ---------- */

        .ar-photo-block {
          margin-bottom: 22px;
        }

        .ar-photo-label {
          font-size: 12.5px;
          color: var(--text-soft);
          margin-bottom: 10px;
          display: block;
        }

        .ar-photo-frame {
          position: relative;
          width: 108px;
          height: 108px;
          cursor: pointer;
          background: #e2e8f0;
          overflow: hidden;
        }

        .ar-photo-frame img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .ar-photo-empty {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 6px;
          color: #64748b;
        }

        .ar-photo-empty span {
          font-size: 10.5px;
          text-align: center;
          padding: 0 8px;
          color: var(--text-soft);
        }

        .ar-corner {
          position: absolute;
          width: 14px;
          height: 14px;
          border-color: var(--accent);
          border-style: solid;
          z-index: 2;
        }

        .ar-corner.tl {
          top: -1px;
          left: -1px;
          border-width: 2px 0 0 2px;
        }

        .ar-corner.tr {
          top: -1px;
          right: -1px;
          border-width: 2px 2px 0 0;
        }

        .ar-corner.bl {
          bottom: -1px;
          left: -1px;
          border-width: 0 0 2px 2px;
        }

        .ar-corner.br {
          bottom: -1px;
          right: -1px;
          border-width: 0 2px 2px 0;
        }

        /* ---------- Consent ---------- */

        .ar-consent {
          display: flex;
          align-items: flex-start;
          gap: 11px;
          margin-bottom: 22px;
          cursor: pointer;
        }

        .ar-check {
          width: 19px;
          height: 19px;
          border-radius: 5px;
          border: 1.5px solid var(--paper-line);
          background: #fff;

          display: flex;
          align-items: center;
          justify-content: center;

          flex-shrink: 0;
          margin-top: 1px;

          transition:
            background 0.15s ease,
            border-color 0.15s ease;
        }

        .ar-check.checked {
          background: var(--ink);
          border-color: var(--ink);
        }

        .ar-consent p {
          font-size: 13.5px;
          line-height: 1.5;
          color: var(--text-soft);
          margin: 0;
        }

        /* ---------- Message ---------- */

        .ar-msg {
          font-size: 13.5px;
          padding: 11px 14px;
          border-radius: 10px;
          margin-bottom: 20px;
        }

        .ar-msg.error {
          background: #fef2f2;
          color: #b91c1c;
          border: 1px solid #fecaca;
        }

        .ar-msg.success {
          background: #ecfdf5;
          color: #047857;
          border: 1px solid #a7f3d0;
        }

        /* ---------- Submit ---------- */

        .ar-submit {
          width: 100%;
          border: none;
          border-radius: 999px;
          padding: 15px;

          font-family: inherit;
          font-size: 15px;
          font-weight: 600;

          color: #ffffff;
          background: var(--accent);
          cursor: pointer;

          transition:
            transform 0.1s ease,
            background 0.15s ease;
        }

        .ar-submit:hover {
          background: var(--accent-deep);
        }

        .ar-submit:active {
          transform: scale(0.98);
        }

        .ar-submit:disabled {
          background: #a5b4fc;
          cursor: not-allowed;
        }

        /* ---------- Confirmation ---------- */

        .ar-done {
          text-align: center;
          padding: 20px 10px 6px;
        }

        .ar-done-badge {
          width: 52px;
          height: 52px;
          border-radius: 50%;
          background: var(--ink);
          color: #a5b4fc;

          display: flex;
          align-items: center;
          justify-content: center;

          margin: 0 auto 16px;
        }

        .ar-done h3 {
          font-family:
            ui-serif,
            Georgia,
            Cambria,
            "Times New Roman",
            serif;

          font-weight: 500;
          font-size: 20px;
          margin: 0 0 6px;
        }

        .ar-done p {
          font-size: 14px;
          color: var(--text-soft);
          max-width: 38ch;
          margin: 0 auto;
          line-height: 1.55;
        }

        @media (max-width: 620px) {
          .ar-root {
            padding: 28px 14px 50px;
          }

          .ar-ticket {
            grid-template-columns: 1fr;
          }

          .ar-stub {
            padding: 18px 24px 22px;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
            border-top: 1px dashed rgba(255, 255, 255, 0.18);
          }

          .ar-stub::before {
            display: none;
          }

          .ar-ticket-main {
            padding: 28px 24px 24px;
          }

          .ar-title {
            font-size: 30px;
          }

          .ar-form {
            padding: 26px 22px 30px;
          }

          .ar-hole {
            display: none;
          }
        }
      `}</style>

      <div className="ar-shell">
        {/* BACK */}
        <button
          className="ar-back"
          type="button"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={16} />
          {t("activityRegister.back")}
        </button>

        {/* =================================================
            TICKET
        ================================================= */}

        <div className="ar-ticket">
          <div className="ar-ticket-main">
            <div className="ar-ref">
              {activity?.id
                ? `N° ${String(activity.id).slice(0, 8)}`
                : "ALEOA"}
            </div>

            <div className="ar-moon">
              <CalendarDays size={17} />
            </div>

            <h1 className="ar-title">
              {activity?.title || "-"}
            </h1>

            <p className="ar-subtitle">
              {t("activityRegister.registration")}
            </p>

            <p className="ar-desc">
              {activity?.description || "-"}
            </p>
          </div>

          <div className="ar-stub">
            <div className="ar-hole top" />

            <div className="ar-stub-item">
              <CalendarDays size={15} />
              <span>
                {activity?.activity_date || "-"}
              </span>
            </div>

            <div className="ar-stub-item">
              <Clock size={15} />
              <span>
                {activity?.activity_time || "-"}
              </span>
            </div>

            <div className="ar-stub-item">
              <MapPin size={15} />
              <span>
                {activity?.location || "-"}
              </span>
            </div>

            <div className="ar-hole bottom" />
          </div>
        </div>

        {/* =================================================
            CLOSED
        ================================================= */}

        {!registrationOpen ? (
          <div className="ar-closed">
            <p className="ar-closed-title">
              {t("activityRegister.errors.registrationClosed")}
            </p>

            <p className="ar-closed-body">
              {t("activityRegister.closedDescription")}
            </p>
          </div>
        ) : messageType === "success" ? (
          <div className="ar-form">
            <div className="ar-done">
              <div className="ar-done-badge">
                <Check size={24} />
              </div>

              <h3>
                {t("activityRegister.successTitle", {
                  defaultValue: "Inscription confirmée",
                })}
              </h3>

              <p>
                {message ||
                  t("activityRegister.success")}
              </p>
            </div>
          </div>
        ) : (
          <form
            className="ar-form"
            onSubmit={handleSubmit}
          >
            <div className="ar-form-head">
              <h2>
                {t("activityRegister.formTitle")}
              </h2>

              <p>
                {t("activityRegister.formDescription")}
              </p>
            </div>

            {/* NOM / PRENOM */}
            <div className="ar-row">
              <div className="ar-field">
                <label>
                  {t("activityRegister.lastName")}
                </label>

                <input
                  type="text"
                  name="nom"
                  value={form.nom}
                  onChange={handleChange}
                  required
                  autoComplete="family-name"
                  placeholder={t(
                    "activityRegister.lastNamePlaceholder"
                  )}
                />
              </div>

              <div className="ar-field">
                <label>
                  {t("activityRegister.firstName")}
                </label>

                <input
                  type="text"
                  name="prenom"
                  value={form.prenom}
                  onChange={handleChange}
                  required
                  autoComplete="given-name"
                  placeholder={t(
                    "activityRegister.firstNamePlaceholder"
                  )}
                />
              </div>
            </div>

            {/* EMAIL / TELEPHONE */}
            <div className="ar-row">
              <div className="ar-field">
                <label>
                  {t("activityRegister.email")}
                </label>

                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  autoComplete="email"
                  placeholder={t(
                    "activityRegister.emailPlaceholder"
                  )}
                />
              </div>

              <div className="ar-field">
                <label>
                  {t("activityRegister.phone")}
                </label>

                <input
                  type="tel"
                  name="telephone"
                  value={form.telephone}
                  onChange={handleChange}
                  required
                  autoComplete="tel"
                  placeholder={t(
                    "activityRegister.phonePlaceholder"
                  )}
                />
              </div>
            </div>

            {/* DATE / VILLE NAISSANCE */}
            <div className="ar-row">
              <div className="ar-field">
                <label>
                  {t("activityRegister.birthDate")}
                </label>

                <input
                  type="date"
                  name="date_naissance"
                  value={form.date_naissance}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="ar-field">
                <label>
                  {t("activityRegister.birthCity")}
                </label>

                <input
                  type="text"
                  name="ville_naissance"
                  value={form.ville_naissance}
                  onChange={handleChange}
                  required
                  placeholder={t(
                    "activityRegister.birthCityPlaceholder"
                  )}
                />
              </div>
            </div>

            {/* =================================================
                DYNAMIC YES / NO QUESTION
            ================================================= */}

            {hasDynamicQuestion && (
              <div
                className="ar-question-block"
                dir={questionDirection}
              >
                <label className="ar-question-label">
                  {dynamicQuestion}
                </label>

                <div className="ar-question-options">
                  <label
                    className={`ar-question-option ${
                      form.extra_question_answer ===
                      "yes"
                        ? "selected"
                        : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="extra_question_answer"
                      value="yes"
                      checked={
                        form.extra_question_answer ===
                        "yes"
                      }
                      onChange={handleChange}
                      required
                    />

                    <span>{yesLabel}</span>
                  </label>

                  <label
                    className={`ar-question-option ${
                      form.extra_question_answer ===
                      "no"
                        ? "selected"
                        : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="extra_question_answer"
                      value="no"
                      checked={
                        form.extra_question_answer ===
                        "no"
                      }
                      onChange={handleChange}
                    />

                    <span>{noLabel}</span>
                  </label>
                </div>
              </div>
            )}

            {/* PHOTO */}
            <div className="ar-photo-block">
              <label className="ar-photo-label">
                {t("activityRegister.photo")}
              </label>

              <div
                className="ar-photo-frame"
                onClick={() =>
                  fileRef.current?.click()
                }
              >
                <div className="ar-corner tl" />
                <div className="ar-corner tr" />
                <div className="ar-corner bl" />
                <div className="ar-corner br" />

                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt={t(
                      "activityRegister.photo"
                    )}
                  />
                ) : (
                  <div className="ar-photo-empty">
                    <Camera size={20} />

                    <span>
                      {t(
                        "activityRegister.photoChoose"
                      )}
                    </span>
                  </div>
                )}

                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handlePhotoChange}
                  style={{ display: "none" }}
                />
              </div>

              <p className="mt-2 text-[11px] text-slate-400">
                {t("activityRegister.photoHint")}
              </p>
            </div>

            {/* CONSENT */}
            <label className="ar-consent">
              <div
                className={`ar-check ${
                  consent ? "checked" : ""
                }`}
              >
                {consent && (
                  <Check
                    size={13}
                    color="#ffffff"
                  />
                )}
              </div>

              <input
                type="checkbox"
                checked={consent}
                onChange={(e) =>
                  setConsent(e.target.checked)
                }
                className="sr-only"
              />

              <p>
                {t("activityRegister.consent")}
              </p>
            </label>

            {/* MESSAGE */}
            {message && (
              <div
                className={`ar-msg ${
                  messageType === "success"
                    ? "success"
                    : "error"
                }`}
              >
                {message}
              </div>
            )}

            {/* SUBMIT */}
            <button
              className="ar-submit"
              type="submit"
              disabled={sending}
            >
              {sending
                ? t("activityRegister.sending")
                : t("activityRegister.submit")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
